import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { dealerService, type Dealer as DBDealer, type CreateDealerData } from '../lib/dealerService';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';

interface User {
  id: string;
  name: string;
  email: string;
  phone_number?: string;
  role: 'admin' | 'branch_manager' | 'inventory_manager';
  assignedBranch?: string;
  status: 'Active' | 'Inactive';
  lastLogin: string;
  profilePhoto?: string;
}

interface Branch {
  id: string;
  branch_name: string;
  address: string;
  phone: string;
  manager_name: string;
  is_active: boolean;
  created_at: string;
}

interface Dealer {
  id: string;
  dealerName: string;
  shopName: string;
  shopAddress: string;
  phoneNumber: string;
  email?: string;
  gstNumber?: string;
  creditTerms: number; // days
  notes?: string;
  totalBusiness: number;
  outstandingBalance: number;
  paymentStatus: 'Good' | 'Overdue' | 'New';
  registrationDate: string;
  lastOrderDate: string;
  averageOrderValue: number;
  totalOrders: number;
}

const Settings: React.FC = () => {
  const { user, token } = useAuth();
  const { triggerBranchRefresh } = useBranch();
  

  // State management
  const [activeTab, setActiveTab] = useState('Profile Settings');
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [profileUpdateStatus, setProfileUpdateStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  const [passwordUpdateStatus, setPasswordUpdateStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  
  // Profile settings states
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '+91 98765 43210' // This will be fetched from profile API later
  });

  // Current user info (using profile data when available, fallback to auth context)
  const currentUser = {
    role: user?.role || 'admin',
    name: profileData.name || user?.name || 'User',
    email: profileData.email || user?.email || '',
    phone: profileData.phone || '',
    accountCreated: '2023-03-15',
    lastLogin: user?.lastLogin || new Date().toISOString()
  };

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // User management states
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  
  // Add User modal dropdown states
  const [isUserRoleDropdownOpen, setIsUserRoleDropdownOpen] = useState(false);
  const [isUserBranchDropdownOpen, setIsUserBranchDropdownOpen] = useState(false);
  
  // Edit User modal dropdown states
  const [isEditUserRoleDropdownOpen, setIsEditUserRoleDropdownOpen] = useState(false);
  const [isEditUserBranchDropdownOpen, setIsEditUserBranchDropdownOpen] = useState(false);
  
  // Add User modal form states
  const [selectedUserRole, setSelectedUserRole] = useState<'admin' | 'branch_manager' | 'inventory_manager'>('branch_manager');
  const [selectedUserBranchId, setSelectedUserBranchId] = useState<string>('');
  
  // Add User modal form data
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    phone_number: '',
    password: '',
    confirmPassword: ''
  });
  
  // Edit User modal form data
  const [editUserData, setEditUserData] = useState({
    name: '',
    email: '',
    phone_number: '',
    role: 'branch_manager' as 'admin' | 'branch_manager' | 'inventory_manager',
    branchId: '',
    password: '',
    confirmPassword: ''
  });
  
  // Add User modal form states
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [userSubmissionStatus, setUserSubmissionStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  
  // Edit User modal form states
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [editUserSubmissionStatus, setEditUserSubmissionStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  
  // Add User modal dropdown options
  const userRoleOptions: {value: 'admin' | 'branch_manager' | 'inventory_manager', label: string}[] = [
    { value: 'admin', label: 'Admin' },
    { value: 'branch_manager', label: 'Branch Manager' },
    { value: 'inventory_manager', label: 'Inventory Manager' }
  ];
  
  // Dynamic branch options will be computed from branchData below
  
  // Branch management states
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [isAddBranchModalOpen, setIsAddBranchModalOpen] = useState(false);
  const [isEditBranchModalOpen, setIsEditBranchModalOpen] = useState(false);
  const [isDeleteBranchModalOpen, setIsDeleteBranchModalOpen] = useState(false);
  const [selectedBranchForEdit, setSelectedBranchForEdit] = useState<any>(null);
  const [selectedBranchForDelete, setSelectedBranchForDelete] = useState<any>(null);
  
  // Add Branch modal form data
  const [newBranchData, setNewBranchData] = useState({
    branch_name: '',
    address: '',
    phone: '',
    manager_name: ''
  });
  
  // Edit Branch modal form data
  const [editBranchData, setEditBranchData] = useState({
    branch_name: '',
    address: '',
    phone: '',
    manager_name: ''
  });
  
  // Add Branch modal form states
  const [isSubmittingBranch, setIsSubmittingBranch] = useState(false);
  const [branchSubmissionStatus, setBranchSubmissionStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  
  // Edit Branch modal form states
  const [isUpdatingBranch, setIsUpdatingBranch] = useState(false);
  const [editBranchSubmissionStatus, setEditBranchSubmissionStatus] = useState<{type: 'success' | 'error' | null, message: string}>({type: null, message: ''});
  
  // Delete Branch modal states
  const [isDeletingBranch, setIsDeletingBranch] = useState(false);

  // Dealer management states
  const [selectedDealers, setSelectedDealers] = useState<string[]>([]);
  const [isAddDealerModalOpen, setIsAddDealerModalOpen] = useState(false);
  const [dealerFormData, setDealerFormData] = useState<CreateDealerData>({
    name: '',
    shop_name: '',
    shop_address: '',
    phone: ''
  });
  const [dealerSubmitting, setDealerSubmitting] = useState(false);
  const [dealerError, setDealerError] = useState<string | null>(null);
  const [dealersList, setDealersList] = useState<DBDealer[]>([]);
  const [isLoadingDealers, setIsLoadingDealers] = useState(false);
  const [editingDealer, setEditingDealer] = useState<DBDealer | null>(null);
  const [isEditDealerModalOpen, setIsEditDealerModalOpen] = useState(false);
  const [isDeletingDealer, setIsDeletingDealer] = useState(false);
  const [dealerToDelete, setDealerToDelete] = useState<DBDealer | null>(null);
  
  // Pagination states for dealers
  const [dealersPagination, setDealersPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [dealersSearchTerm, setDealersSearchTerm] = useState('');

  // Company settings states
  const [companyData, setCompanyData] = useState({
    id: null as number | null,
    company_name: '',
    gst_number: '',
    address: '',
    phone_number: '',
    email_address: ''
  });
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companySuccess, setCompanySuccess] = useState<string | null>(null);


  // Load profile data from database
  const loadProfileData = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${getApiUrl("profile")}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfileData({
          name: data.user.name || '',
          email: data.user.email || '',
          phone: data.user.phoneNumber || ''
        });
      } else {
        console.error('Failed to load profile data:', data.error);
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
    }
  };

  // Load company data from database
  const loadCompanyData = async () => {
    if (!token || currentUser.role !== 'admin') return;
    
    setIsLoadingCompany(true);
    setCompanyError(null);
    
    try {
      const response = await fetch(`${getApiUrl("companies")}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        if (data.company) {
          setCompanyData({
            id: data.company.id,
            company_name: data.company.company_name || '',
            gst_number: data.company.gst_number ?? '',
            address: data.company.address || '',
            phone_number: data.company.phone_number || '',
            email_address: data.company.email_address || ''
          });
        }
      } else {
        console.error('Failed to load company data:', data.error);
        setCompanyError('Failed to load company information');
      }
    } catch (error) {
      console.error('Company fetch error:', error);
      setCompanyError('Failed to load company information');
    } finally {
      setIsLoadingCompany(false);
    }
  };

  // Save company data to database
  const saveCompanyData = async () => {
    if (!token || currentUser.role !== 'admin') return;
    
    // Validation
    if (!companyData.company_name.trim()) {
      setCompanyError('Company name is required');
      return;
    }
    
    setIsSavingCompany(true);
    setCompanyError(null);
    setCompanySuccess(null);
    
    try {
      // Use PUT method with company ID if updating existing company
      const method = companyData.id ? 'PUT' : 'POST';
      const url = companyData.id ? `${getApiUrl("companies")}/${companyData.id}` : getApiUrl("companies");
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: companyData.company_name.trim(),
          gst_number: companyData.gst_number.trim() || null,
          address: companyData.address.trim(),
          phone_number: companyData.phone_number.trim(),
          email_address: companyData.email_address.trim()
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setCompanySuccess('Company information saved successfully!');
        // Reload company data to get updated information
        loadCompanyData();
      } else {
        console.error('Failed to save company data:', data.error);
        setCompanyError(data.error || 'Failed to save company information');
      }
    } catch (error) {
      console.error('Company save error:', error);
      setCompanyError('Failed to save company information');
    } finally {
      setIsSavingCompany(false);
    }
  };



  // Load users data from database
  const loadUsersData = async () => {
    if (!token || currentUser.role !== 'admin') return;
    
    setIsLoadingUsers(true);
    try {
      const response = await fetch(`${getApiUrl("users")}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Loaded users:', data.users);
        setUserData(data.users || []);
      } else {
        console.error('Failed to load users data:', data.error);
      }
    } catch (error) {
      console.error('Users fetch error:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Create new user
  const createNewUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) return;
    
    // Validation
    if (!newUserData.name.trim() || !newUserData.email.trim() || !newUserData.phone_number.trim() || !newUserData.password || !newUserData.confirmPassword) {
      setUserSubmissionStatus({type: 'error', message: 'All fields are required'});
      return;
    }
    
    if (newUserData.password !== newUserData.confirmPassword) {
      setUserSubmissionStatus({type: 'error', message: 'Passwords do not match'});
      return;
    }
    
    if (newUserData.password.length < 6) {
      setUserSubmissionStatus({type: 'error', message: 'Password must be at least 6 characters long'});
      return;
    }
    
    // Validate branch assignment for branch managers
    if (selectedUserRole === 'branch_manager' && !selectedUserBranchId) {
      setUserSubmissionStatus({type: 'error', message: 'Branch assignment is required for branch managers'});
      return;
    }
    
    setIsSubmittingUser(true);
    setUserSubmissionStatus({type: null, message: ''});
    
    try {
      const requestBody = {
        name: newUserData.name.trim(),
        email: newUserData.email.trim(),
        phone_number: newUserData.phone_number.trim(),
        password: newUserData.password,
        role: selectedUserRole,
        branchId: selectedUserRole === 'branch_manager' ? selectedUserBranchId : null
      };

      const response = await fetch(`${getApiUrl("users")}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUserSubmissionStatus({type: 'success', message: 'User created successfully!'});
        
        // Reset form
        setNewUserData({
          name: '',
          email: '',
          phone_number: '',
          password: '',
          confirmPassword: ''
        });
        setSelectedUserRole('branch_manager');
        setSelectedUserBranchId('');
        
        // Reload users data
        loadUsersData();
        
        // Close modal after a short delay
        setTimeout(() => {
          setIsAddUserModalOpen(false);
          setUserSubmissionStatus({type: null, message: ''});
        }, 1500);
        
      } else {
        setUserSubmissionStatus({type: 'error', message: data.error || 'Failed to create user'});
      }
    } catch (error) {
      console.error('User creation error:', error);
      setUserSubmissionStatus({type: 'error', message: 'Network error. Please try again.'});
    } finally {
      setIsSubmittingUser(false);
    }
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    console.log('Editing user:', user);
    console.log('Available branches:', userBranchOptions);
    console.log('User assigned branch:', user.assignedBranch);
    
    // Find the branch ID that matches the assigned branch name
    const matchingBranch = userBranchOptions.find(branch => branch.name === user.assignedBranch);
    const branchId = matchingBranch ? matchingBranch.id : '';
    
    console.log('Matching branch found:', matchingBranch);
    console.log('Setting branchId to:', branchId);
    
    setEditingUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      phone_number: user.phone_number || '',
      role: user.role,
      branchId: branchId,
      password: '',
      confirmPassword: ''
    });
    setIsEditUserModalOpen(true);
  };

  // Handle delete user
  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
  };

  // Update user
  const updateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !editingUser) return;
    
    // Validation
    if (!editUserData.name.trim() || !editUserData.email.trim() || !editUserData.phone_number.trim()) {
      setEditUserSubmissionStatus({type: 'error', message: 'Name, email and phone number are required'});
      return;
    }
    
    // Validate branch assignment for branch managers
    if (editUserData.role === 'branch_manager' && !editUserData.branchId) {
      setEditUserSubmissionStatus({type: 'error', message: 'Branch assignment is required for branch managers'});
      return;
    }
    
    // Validate password if provided
    if (editUserData.password || editUserData.confirmPassword) {
      if (editUserData.password.length < 6) {
        setEditUserSubmissionStatus({type: 'error', message: 'Password must be at least 6 characters long'});
        return;
      }
      if (editUserData.password !== editUserData.confirmPassword) {
        setEditUserSubmissionStatus({type: 'error', message: 'Passwords do not match'});
        return;
      }
    }
    
    setIsUpdatingUser(true);
    setEditUserSubmissionStatus({type: null, message: ''});
    
    try {
      const requestBody: any = {
        name: editUserData.name.trim(),
        email: editUserData.email.trim(),
        phone_number: editUserData.phone_number.trim(),
        role: editUserData.role,
        branchId: editUserData.role === 'branch_manager' ? editUserData.branchId : null
      };
      
      // Only include password if it's provided
      if (editUserData.password.trim()) {
        requestBody.password = editUserData.password.trim();
      }

      const response = await fetch(`${getApiUrl(`users/${editingUser.id}`)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setEditUserSubmissionStatus({type: 'success', message: 'User updated successfully!'});
        
        // Reload users data
        loadUsersData();
        
        // Close modal after a short delay
        setTimeout(() => {
          setIsEditUserModalOpen(false);
          setEditUserSubmissionStatus({type: null, message: ''});
          setEditingUser(null);
        }, 1500);
        
      } else {
        setEditUserSubmissionStatus({type: 'error', message: data.error || 'Failed to update user'});
      }
    } catch (error) {
      console.error('User update error:', error);
      setEditUserSubmissionStatus({type: 'error', message: 'Network error. Please try again.'});
    } finally {
      setIsUpdatingUser(false);
    }
  };

  // Delete user
  const deleteUser = async () => {
    if (!token || !userToDelete) return;
    
    setIsDeletingUser(true);
    
    try {
      const response = await fetch(`${getApiUrl(`users/${userToDelete.id}`)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Reload users data
        loadUsersData();
        
        // Close modal
        setIsDeletingUser(false);
        setUserToDelete(null);
        
      } else {
        console.error('Failed to delete user:', data.error);
        setIsDeletingUser(false);
        setUserToDelete(null);
      }
    } catch (error) {
      console.error('User deletion error:', error);
      setIsDeletingUser(false);
      setUserToDelete(null);
    }
  };

  // Load branches data from database
  const loadBranchesData = async () => {
    if (!token || currentUser.role !== 'admin') return;
    
    setIsLoadingBranches(true);
    try {
      const response = await fetch(`${getApiUrl("branches")}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Loaded branches:', data.branches);
        setBranchData(data.branches || []);
      } else {
        console.error('Failed to load branches data:', data.error);
      }
    } catch (error) {
      console.error('Branches fetch error:', error);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  // Create new branch
  const createNewBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) return;
    
    // Validation
    if (!newBranchData.branch_name.trim() || !newBranchData.address.trim() || !newBranchData.phone.trim()) {
      setBranchSubmissionStatus({type: 'error', message: 'Branch name, address, and phone are required'});
      return;
    }
    
    setIsSubmittingBranch(true);
    setBranchSubmissionStatus({type: null, message: ''});
    
    try {
      const requestBody = {
        branch_name: newBranchData.branch_name.trim(),
        address: newBranchData.address.trim(),
        phone: newBranchData.phone.trim(),
        manager_name: newBranchData.manager_name.trim() || null
      };

      const response = await fetch(`${getApiUrl("branches")}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBranchSubmissionStatus({type: 'success', message: 'Branch created successfully!'});
        showToast('Branch created successfully!', 'success');
        
        // Reset form
        setNewBranchData({
          branch_name: '',
          address: '',
          phone: '',
          manager_name: ''
        });
        
        // Reload branches data
        loadBranchesData();
        
        // Trigger refresh of branch dropdown in Layout
        triggerBranchRefresh();
        
        // Close modal after a short delay
        setTimeout(() => {
          setIsAddBranchModalOpen(false);
          setBranchSubmissionStatus({type: null, message: ''});
        }, 1500);
        
      } else {
        setBranchSubmissionStatus({type: 'error', message: data.error || 'Failed to create branch'});
      }
    } catch (error) {
      console.error('Branch creation error:', error);
      setBranchSubmissionStatus({type: 'error', message: 'Network error. Please try again.'});
    } finally {
      setIsSubmittingBranch(false);
    }
  };

  // Update branch function
  const updateBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !selectedBranchForEdit) return;
    
    // Validation
    if (!editBranchData.branch_name.trim() || !editBranchData.address.trim() || !editBranchData.phone.trim()) {
      setEditBranchSubmissionStatus({type: 'error', message: 'Branch name, address, and phone are required'});
      return;
    }
    
    setIsUpdatingBranch(true);
    setEditBranchSubmissionStatus({type: null, message: ''});
    
    try {
      const requestBody = {
        branch_name: editBranchData.branch_name.trim(),
        address: editBranchData.address.trim(),
        phone: editBranchData.phone.trim(),
        manager_name: editBranchData.manager_name.trim() || null
      };

      const response = await fetch(`${getApiUrl(`branches/${selectedBranchForEdit.id}`)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setEditBranchSubmissionStatus({type: 'success', message: 'Branch updated successfully!'});
        showToast('Branch updated successfully!', 'success');
        
        // Reload branches data
        loadBranchesData();
        
        // Close modal after a short delay
        setTimeout(() => {
          setIsEditBranchModalOpen(false);
          setEditBranchSubmissionStatus({type: null, message: ''});
          setSelectedBranchForEdit(null);
        }, 1500);
        
      } else {
        setEditBranchSubmissionStatus({type: 'error', message: data.error || 'Failed to update branch'});
      }
    } catch (error) {
      console.error('Branch update error:', error);
      setEditBranchSubmissionStatus({type: 'error', message: 'Network error. Please try again.'});
    } finally {
      setIsUpdatingBranch(false);
    }
  };

  // Delete branch function
  const deleteBranch = async () => {
    if (!token || !selectedBranchForDelete) return;
    
    setIsDeletingBranch(true);
    
    try {
      const response = await fetch(getApiUrl(`branches/${selectedBranchForDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Reload branches data
        loadBranchesData();
        
        // Close modal
        setIsDeleteBranchModalOpen(false);
        setSelectedBranchForDelete(null);
        
        // Show success toast
        showToast('Branch deleted successfully!', 'success');
        
      } else {
        console.error('Failed to delete branch:', data.error);
        showToast('Failed to delete branch: ' + (data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Branch deletion error:', error);
      showToast('Network error. Please try again.', 'error');
    } finally {
      setIsDeletingBranch(false);
    }
  };

  // Handle edit branch click
  const handleEditBranch = (branch: any) => {
    setSelectedBranchForEdit(branch);
    setEditBranchData({
      branch_name: branch.branch_name || '',
      address: branch.address || '',
      phone: branch.phone || '',
      manager_name: branch.manager_name || ''
    });
    setIsEditBranchModalOpen(true);
  };

  // Handle delete branch click
  const handleDeleteBranch = (branch: any) => {
    setSelectedBranchForDelete(branch);
    setIsDeleteBranchModalOpen(true);
  };

  // Handle opening add user modal
  const handleOpenAddUserModal = () => {
    // Set default branch if available
    if (userBranchOptions.length > 0) {
      setSelectedUserBranchId(userBranchOptions[0].id);
    }
    setIsAddUserModalOpen(true);
  };

  // Load profile data, users data, branches data, and company data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      setIsPageLoading(true);
      try {
        await loadProfileData();
        if (currentUser.role === 'admin') {
          await Promise.all([
            loadUsersData(),
            loadBranchesData(),
            loadCompanyData()
          ]);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setIsPageLoading(false);
      }
    };

    loadInitialData();
  }, [token, currentUser.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle outside clicks to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Close dropdowns if clicking outside
      if (!target.closest('[data-dropdown]')) {
        setIsUserRoleDropdownOpen(false);
        setIsUserBranchDropdownOpen(false);
        setIsEditUserRoleDropdownOpen(false);
        setIsEditUserBranchDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // API Functions
  const updateProfile = async () => {
    if (!token) return;
    
    setIsLoading(true);
    setProfileUpdateStatus({type: null, message: ''});
    
    try {
      const response = await fetch(`${getApiUrl("profile")}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profileData.name,
          phoneNumber: profileData.phone
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProfileUpdateStatus({type: 'success', message: 'Profile updated successfully!'});
        // Update the profile data with the response
        setProfileData({
          name: data.user.name || '',
          email: data.user.email || '',
          phone: data.user.phoneNumber || ''
        });
      } else {
        setProfileUpdateStatus({type: 'error', message: data.error || 'Failed to update profile'});
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setProfileUpdateStatus({type: 'error', message: 'Network error. Please try again.'});
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async () => {
    if (!token) return;
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordUpdateStatus({type: 'error', message: 'New passwords do not match'});
      return;
    }
    
    setIsLoading(true);
    setPasswordUpdateStatus({type: null, message: ''});
    
    try {
      const response = await fetch(`${getApiUrl("profile/password")}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordUpdateStatus({type: 'success', message: 'Password updated successfully!'});
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordUpdateStatus({type: 'error', message: data.error || 'Failed to update password'});
      }
    } catch (error) {
      console.error('Password update error:', error);
      setPasswordUpdateStatus({type: 'error', message: 'Network error. Please try again.'});
    } finally {
      setIsLoading(false);
    }
  };

  // Define tabs based on user role
  const getAvailableTabs = () => {
    const baseTabs = ['Profile Settings'];
    
    if (currentUser.role === 'admin') {
      return [...baseTabs, 'User Management', 'Dealer Management', 'Branch Management', 'My Company Details'];
    }
    
    return baseTabs;
  };

  const availableTabs = getAvailableTabs();

  // User data state
  const [userData, setUserData] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Branch data state
  const [branchData, setBranchData] = useState<Branch[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };
  
  // Dynamic branch options from database
  const userBranchOptions = branchData.map(branch => ({
    id: branch.id,
    name: branch.branch_name
  }));

  // Sample data for dealer management
  const dealerData: Dealer[] = [
    {
      id: 'DEALER001',
      dealerName: 'Rajesh Electronics',
      shopName: 'Mobile World',
      shopAddress: 'Shop No. 15, Electronics Market, Mumbai, MH 400001',
      phoneNumber: '+91 98765 43210',
      email: 'rajesh@mobileworld.com',
      gstNumber: '27ABCDE1234F1Z5',
      creditTerms: 30,
      totalBusiness: 850000,
      outstandingBalance: 125000,
      paymentStatus: 'Good',
      registrationDate: '2023-03-15',
      lastOrderDate: '2024-01-15',
      averageOrderValue: 45000,
      totalOrders: 19,
      notes: 'Bulk buyer, good payment history'
    },
    {
      id: 'DEALER002',
      dealerName: 'Amit Kumar',
      shopName: 'TechZone Solutions',
      shopAddress: 'A-123, Connaught Place, Delhi, DL 110001',
      phoneNumber: '+91 87654 32109',
      email: 'amit@techzone.com',
      creditTerms: 15,
      totalBusiness: 650000,
      outstandingBalance: 85000,
      paymentStatus: 'Overdue',
      registrationDate: '2023-06-20',
      lastOrderDate: '2024-01-10',
      averageOrderValue: 38500,
      totalOrders: 17,
      notes: 'Payment delays recently'
    },
    {
      id: 'DEALER003',
      dealerName: 'Priya Sharma',
      shopName: 'Digital Hub',
      shopAddress: 'Plot 45, IT Park, Bangalore, KA 560001',
      phoneNumber: '+91 76543 21098',
      gstNumber: '29ABCDE1234F1Z5',
      creditTerms: 45,
      totalBusiness: 420000,
      outstandingBalance: 65000,
      paymentStatus: 'Good',
      registrationDate: '2023-09-10',
      lastOrderDate: '2024-01-08',
      averageOrderValue: 35000,
      totalOrders: 12
    },
    {
      id: 'DEALER004',
      dealerName: 'Vikash Mobile',
      shopName: 'Smart Phones Plus',
      shopAddress: 'Shop 78, City Mall, Chennai, TN 600001',
      phoneNumber: '+91 65432 10987',
      creditTerms: 21,
      totalBusiness: 125000,
      outstandingBalance: 35000,
      paymentStatus: 'New',
      registrationDate: '2024-01-05',
      lastOrderDate: '2024-01-05',
      averageOrderValue: 41667,
      totalOrders: 3,
      notes: 'New dealer, first orders'
    },
    {
      id: 'DEALER005',
      dealerName: 'Sneha Communications',
      shopName: 'Mobile Palace',
      shopAddress: 'Sector 18, Noida, UP 201301',
      phoneNumber: '+91 54321 09876',
      email: 'sneha@mobilepalace.com',
      gstNumber: '09ABCDE1234F1Z5',
      creditTerms: 30,
      totalBusiness: 380000,
      outstandingBalance: 95000,
      paymentStatus: 'Overdue',
      registrationDate: '2023-07-25',
      lastOrderDate: '2023-12-20',
      averageOrderValue: 31667,
      totalOrders: 12,
      notes: 'Good volume, payment follow-up needed'
    }
  ];

  // Helper functions
  const getRoleBadge = (role: string) => {
    const roleConfig = {
      'admin': 'bg-red-100 text-red-800',
      'branch_manager': 'bg-blue-100 text-blue-800',
      'inventory_manager': 'bg-green-100 text-green-800'
    };
    return roleConfig[role as keyof typeof roleConfig] || 'bg-gray-100 text-gray-800';
  };

  // Dealer management handlers
  const loadDealers = async (page: number = 1, search: string = '') => {
    setIsLoadingDealers(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: dealersPagination.limit.toString(),
        include_stats: 'false'
      });
      
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await fetch(`${getApiUrl("dealers")}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setDealersList(result.dealers || []);
        setDealersPagination({
          page: result.pagination.page,
          limit: result.pagination.limit,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
          hasNextPage: result.pagination.hasNextPage,
          hasPrevPage: result.pagination.hasPrevPage
        });
      } else {
        throw new Error(result.error || 'Failed to load dealers');
      }
    } catch (err) {
      console.error('Error loading dealers:', err);
      // Fallback to dealerService for compatibility
      try {
        const { data, error } = await dealerService.getAllDealers();
        if (error) {
          throw new Error(error.message || 'Failed to load dealers');
        }
        setDealersList(data || []);
      } catch (fallbackErr) {
        console.error('Fallback error:', fallbackErr);
      }
    } finally {
      setIsLoadingDealers(false);
    }
  };

  const handleAddDealer = async (e: React.FormEvent) => {
    e.preventDefault();
    setDealerSubmitting(true);
    setDealerError(null);

    try {
      console.log('Creating dealer with data:', dealerFormData);
      
      // Validate required fields
      if (!dealerFormData.name.trim()) {
        throw new Error('Dealer name is required');
      }
      if (!dealerFormData.phone.trim()) {
        throw new Error('Phone number is required');
      }
      if (!dealerFormData.shop_name || !dealerFormData.shop_name.trim()) {
        throw new Error('Shop name is required');
      }
      if (!dealerFormData.shop_address || !dealerFormData.shop_address.trim()) {
        throw new Error('Shop address is required');
      }

      const { data, error } = await dealerService.createDealer(dealerFormData);
      console.log('Dealer service response:', { data, error });
      
      if (error) {
        throw new Error(error.message || error || 'Failed to create dealer');
      }
      
      // Refresh dealers list to get updated pagination
      loadDealers(1, dealersSearchTerm);
      
      // Reset form and close modal
      setDealerFormData({ name: '', shop_name: '', shop_address: '', phone: '' });
      setIsAddDealerModalOpen(false);
      
      console.log('Dealer created successfully:', data);
      showToast('Dealer created successfully!', 'success');
    } catch (err) {
      console.error('Error creating dealer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create dealer';
      setDealerError(errorMessage);
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setDealerSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'Dealer Management') {
      loadDealers(1, dealersSearchTerm);
    }
  }, [activeTab]);

  // Pagination handlers for dealers
  const handleDealersPageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= dealersPagination.totalPages) {
      loadDealers(newPage, dealersSearchTerm);
    }
  };

  const handleDealersSearch = () => {
    loadDealers(1, dealersSearchTerm);
  };

  const handleDealersSearchClear = () => {
    setDealersSearchTerm('');
    loadDealers(1, '');
  };

  const handleEditDealer = (dealer: DBDealer) => {
    setEditingDealer(dealer);
    setDealerFormData({
      name: dealer.name,
      shop_name: dealer.shop_name,
      shop_address: dealer.shop_address,
      phone: dealer.phone
    });
    setIsEditDealerModalOpen(true);
  };

  const handleUpdateDealer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDealer) return;

    setDealerSubmitting(true);
    setDealerError(null);

    try {
      const { data, error } = await dealerService.updateDealer(editingDealer.id, dealerFormData);
      if (error) {
        throw new Error(error.message || 'Failed to update dealer');
      }
      
      // Refresh dealers list to get updated data
      loadDealers(dealersPagination.page, dealersSearchTerm);
      
      // Reset form and close modal
      setDealerFormData({ name: '', shop_name: '', shop_address: '', phone: '' });
      setEditingDealer(null);
      setIsEditDealerModalOpen(false);
      
      console.log('Dealer updated successfully:', data);
      showToast('Dealer updated successfully!', 'success');
    } catch (err) {
      console.error('Error updating dealer:', err);
      setDealerError(err instanceof Error ? err.message : 'Failed to update dealer');
    } finally {
      setDealerSubmitting(false);
    }
  };

  const handleDeleteDealer = (dealer: DBDealer) => {
    setDealerToDelete(dealer);
  };

  const confirmDeleteDealer = async () => {
    if (!dealerToDelete) return;

    console.log('🗑️ Settings: Starting dealer deletion for:', dealerToDelete);
    setIsDeletingDealer(true);
    try {
      console.log('🗑️ Settings: Calling dealerService.deleteDealer with ID:', dealerToDelete.id);
      const { error } = await dealerService.deleteDealer(dealerToDelete.id);
      console.log('🗑️ Settings: Delete service response:', { error });
      
      if (error) {
        console.error('🚫 Settings: Delete failed with error:', error);
        throw new Error(error.message || error || 'Failed to delete dealer');
      }

      console.log('✅ Settings: Delete successful, refreshing dealers list');
      
      // Refresh dealers list and handle pagination
      const newTotal = dealersPagination.total - 1;
      const currentPageItems = dealersList.length;
      
      // If this was the last item on the current page and we're not on page 1, go to previous page
      const targetPage = (currentPageItems === 1 && dealersPagination.page > 1) 
        ? dealersPagination.page - 1 
        : dealersPagination.page;
      
      loadDealers(targetPage, dealersSearchTerm);
      setDealerToDelete(null);
      
      console.log('Dealer deleted successfully');
      showToast('Dealer deleted successfully!', 'success');
    } catch (err) {
      console.error('🚫 Settings: Error deleting dealer:', err);
      console.error('🚫 Settings: Error details:', {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : typeof err
      });
      
      // Use the specific error message if available, otherwise fallback to generic message
      const errorMessage = err instanceof Error ? err.message : String(err);
      showToast(errorMessage || 'Failed to delete dealer. Please try again.', 'error');
    } finally {
      console.log('🗑️ Settings: Dealer deletion process completed');
      setIsDeletingDealer(false);
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames = {
      'admin': 'Admin',
      'branch_manager': 'Branch Manager',
      'inventory_manager': 'Inventory Manager'
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };

  const getStatusBadge = (status: string) => {
    return status === 'Active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      'Good': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800',
      'New': 'bg-blue-100 text-blue-800'
    };
    return statusConfig[status as keyof typeof statusConfig] || 'bg-gray-100 text-gray-800';
  };

  // Icons
  const UserIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const LockIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  const BuildingIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );

  const CogIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const HandshakeIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  // Render Profile Settings Tab
  const renderProfileSettings = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Information Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <UserIcon />
          <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              value={profileData.name}
              onChange={(e) => setProfileData({...profileData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              value={profileData.phone}
              onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="+91 XXXXX XXXXX"
            />
          </div>

          {/* Profile Update Status */}
          {profileUpdateStatus.type && (
            <div className={`p-3 rounded-lg ${
              profileUpdateStatus.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              <p className="text-sm">{profileUpdateStatus.message}</p>
            </div>
          )}

          <div className="pt-2">
            <button 
              onClick={updateProfile}
              disabled={isLoading || !profileData.name.trim()}
              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : 'Save Personal Info'}
            </button>
          </div>
        </div>
      </div>

      {/* Account Details Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <CogIcon />
          <h3 className="text-lg font-semibold text-gray-900">Account Details</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={profileData.email}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              title="Email address cannot be changed as it is used for login authentication"
            />
            <p className="text-xs text-gray-500 mt-1">Used for login - Cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <div className="relative">
              <button 
                type="button"
                disabled
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed h-10"
              >
                <span className="text-sm">{getRoleDisplayName(currentUser.role)}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">System assigned - Cannot be changed</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Status</label>
              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                Active
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Created</label>
              <p className="text-sm text-gray-900">{new Date(currentUser.accountCreated).toLocaleDateString('en-IN')}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
            <p className="text-sm text-gray-900">{new Date(currentUser.lastLogin).toLocaleString('en-IN')}</p>
          </div>
        </div>
      </div>

      {/* Security Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
        <div className="flex items-center gap-3 mb-4">
          <LockIcon />
          <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
            <input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter current password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter new password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Confirm new password"
            />
          </div>
        </div>

        {/* Password Update Status */}
        {passwordUpdateStatus.type && (
          <div className={`mt-4 p-3 rounded-lg ${
            passwordUpdateStatus.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            <p className="text-sm">{passwordUpdateStatus.message}</p>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button 
            onClick={updatePassword}
            disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );

  // Render User Management Tab (Admin only)
  const renderUserManagement = () => {
    return (
      <div className="space-y-6">
        {/* Page Title */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <button 
            onClick={handleOpenAddUserModal}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Add New User
          </button>
        </div>

        {/* User List Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Branch</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoadingUsers ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <LoadingSpinner size="md" text="Loading users..." />
                  </td>
                </tr>
              ) : userData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <UserIcon />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                      <p className="text-gray-500">Get started by adding your first user to the system.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                userData.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 border-b border-gray-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <UserIcon />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{user.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadge(user.role)}`}>
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.assignedBranch || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(user.lastLogin).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        {user.role !== 'admin' ? (
                          <>
                            <button 
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-900" 
                              title="Edit User"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button 
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900" 
                              title="Delete User"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Protected User</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Render Dealer Management Tab (Admin only)
  const renderDealerManagement = () => {
    return (
      <div className="space-y-6">
      {/* Page Title */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Dealer Management</h2>
        <button 
          onClick={() => setIsAddDealerModalOpen(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          Add New Dealer
        </button>
      </div>

      {/* Search and Pagination Info */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search dealers by name, shop, or phone..."
                value={dealersSearchTerm}
                onChange={(e) => setDealersSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDealersSearch();
                  }
                }}
                className="block w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white hover:border-gray-300 transition-colors"
                style={{ minWidth: '320px' }}
              />
              {dealersSearchTerm && (
                <button
                  onClick={handleDealersSearchClear}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <button
              onClick={handleDealersSearch}
              className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
            >
              Search
            </button>
          </div>

          {/* Pagination Info */}
          {dealersPagination.total > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {dealersPagination.total > 0 ? ((dealersPagination.page - 1) * dealersPagination.limit) + 1 : 0} to{' '}
                {Math.min(dealersPagination.page * dealersPagination.limit, dealersPagination.total)} of{' '}
                {dealersPagination.total} dealers
              </span>
              
              {/* Pagination Controls */}
              {dealersPagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDealersPageChange(dealersPagination.page - 1)}
                    disabled={!dealersPagination.hasPrevPage}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <span className="px-3 py-1 text-sm border border-gray-200 rounded-lg bg-gray-50">
                    {dealersPagination.page} of {dealersPagination.totalPages}
                  </span>
                  
                  <button
                    onClick={() => handleDealersPageChange(dealersPagination.page + 1)}
                    disabled={!dealersPagination.hasNextPage}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dealer List Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
        
        {isLoadingDealers ? (
          <div className="p-6">
            <div className="text-center py-12">
              <div className="mx-auto h-8 w-8 animate-spin text-primary-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="h-full w-full">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <p className="mt-2 text-sm text-gray-500">Loading dealers...</p>
            </div>
          </div>
        ) : dealersList.length === 0 ? (
          <div className="p-6">
            <div className="text-center py-12">
              <div className="mx-auto h-24 w-24 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="h-full w-full">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No dealers found</h3>
              <p className="mt-2 text-sm text-gray-500">Get started by adding your first dealer.</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dealersList.map((dealer) => (
                  <tr key={dealer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{dealer.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dealer.shop_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dealer.phone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={dealer.shop_address}>
                        {dealer.shop_address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(dealer.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => handleEditDealer(dealer)}
                          className="text-blue-600 hover:text-blue-900" 
                          title="Edit Dealer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleDeleteDealer(dealer)}
                          className="text-red-600 hover:text-red-900" 
                          title="Delete Dealer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
      </div>
    );
  };

  // Render Branch Management Tab (Admin only)
  const renderBranchManagement = () => {
    return (
      <div className="space-y-6">
      {/* Page Title */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Branch Management</h2>
          <button 
            onClick={() => setIsAddBranchModalOpen(true)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Add New Branch
          </button>
        </div>

        {/* Branch List Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoadingBranches ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <LoadingSpinner size="md" text="Loading branches..." />
                  </td>
                </tr>
              ) : branchData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <BuildingIcon />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No branches found</h3>
                      <p className="text-gray-500">Get started by adding your first branch to the system.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                branchData.map((branch) => (
                  <tr key={branch.id} className="hover:bg-gray-50 border-b border-gray-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{branch.branch_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{branch.address}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{branch.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{branch.manager_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${branch.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {branch.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(branch.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleEditBranch(branch)}
                          className="text-blue-600 hover:text-blue-900" 
                          title="Edit Branch"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleDeleteBranch(branch)}
                          className="text-red-600 hover:text-red-900" 
                          title="Delete Branch"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    );
  };

  // Render My Company Details Tab (Admin only)
  const renderCompanyDetails = () => (
    <div className="space-y-8">
      {/* Company Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-4 mb-6">
          <CogIcon />
          <h3 className="text-lg font-semibold text-gray-900">My Company Details</h3>
        </div>

        {/* Error/Success Messages */}
        {companyError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
            {companyError}
          </div>
        )}
        
        {companySuccess && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 border border-green-200">
            {companySuccess}
          </div>
        )}

        {isLoadingCompany ? (
          <div className="text-center py-8">
            <LoadingSpinner size="md" text="" />
            <p className="mt-2 text-gray-600">Loading company information...</p>
          </div>
        ) : (
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
            <input
              type="text"
              value={companyData.company_name}
              onChange={(e) => setCompanyData({...companyData, company_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter company name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
            <input
              type="text"
              value={companyData.gst_number}
              onChange={(e) => setCompanyData({...companyData, gst_number: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter GST number"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <textarea
              value={companyData.address}
              onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter company address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              value={companyData.phone_number}
              onChange={(e) => setCompanyData({...companyData, phone_number: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={companyData.email_address}
              onChange={(e) => setCompanyData({...companyData, email_address: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter email address"
            />
          </div>

          <div className="lg:col-span-2 flex justify-end">
            <button 
              onClick={saveCompanyData}
              disabled={isSavingCompany || !companyData.company_name.trim()}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSavingCompany && (
                <LoadingSpinner size="sm" text="" color="white" />
              )}
              {isSavingCompany ? 'Saving...' : 'Save Company Details'}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'Profile Settings':
        return renderProfileSettings();
      case 'User Management':
        return renderUserManagement();
      case 'Dealer Management':
        return renderDealerManagement();
      case 'Branch Management':
        return renderBranchManagement();
      case 'My Company Details':
        return renderCompanyDetails();
      default:
        return renderProfileSettings();
    }
  };

  // Show loading spinner while data is being loaded
  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner size="lg" text="" />
          <p className="text-gray-600 text-lg">Loading Settings...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your profile, users, branches, and system configuration</p>
      </div>

      {/* Horizontal Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {availableTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Add New Branch Modal */}
      {isAddBranchModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsAddBranchModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add New Branch</h2>
              <button
                onClick={() => setIsAddBranchModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="space-y-6" onSubmit={createNewBranch}>
              {/* Branch Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Branch Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch Name *
                    </label>
                    <input
                      type="text"
                      value={newBranchData.branch_name}
                      onChange={(e) => setNewBranchData({...newBranchData, branch_name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter branch name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={newBranchData.phone}
                      onChange={(e) => setNewBranchData({...newBranchData, phone: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    rows={3}
                    value={newBranchData.address}
                    onChange={(e) => setNewBranchData({...newBranchData, address: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                    placeholder="Enter complete branch address with city, state, and pincode"
                  ></textarea>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manager Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={newBranchData.manager_name}
                    onChange={(e) => setNewBranchData({...newBranchData, manager_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="Enter manager name (can be assigned later)"
                  />
                </div>
              </div>

              {/* Status Message */}
              {branchSubmissionStatus.type && (
                <div className={`p-3 rounded-lg ${
                  branchSubmissionStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-700' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  <p className="text-sm">{branchSubmissionStatus.message}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setIsAddBranchModalOpen(false)}
                  disabled={isSubmittingBranch}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingBranch || !newBranchData.branch_name.trim() || !newBranchData.address.trim() || !newBranchData.phone.trim()}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmittingBranch ? 'Creating...' : 'Add Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add New User Modal */}
      {isAddUserModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsAddUserModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add New User</h2>
              <button
                onClick={() => setIsAddUserModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="space-y-6" onSubmit={createNewUser}>
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newUserData.name}
                      onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={newUserData.email}
                      onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={newUserData.phone_number}
                      onChange={(e) => setNewUserData({...newUserData, phone_number: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
              </div>
              
              {/* Account Settings */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role *
                    </label>
                    <div className="relative" data-dropdown>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsUserRoleDropdownOpen(!isUserRoleDropdownOpen);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      >
                        <span className="text-sm text-gray-700">
                          {userRoleOptions.find(option => option.value === selectedUserRole)?.label}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isUserRoleDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isUserRoleDropdownOpen && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {userRoleOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedUserRole(option.value);
                                setIsUserRoleDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                selectedUserRole === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {(selectedUserRole === 'branch_manager') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assigned Branch *
                      </label>
                      <div className="relative" data-dropdown>
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsUserBranchDropdownOpen(!isUserBranchDropdownOpen);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                        >
                          <span className="text-sm text-gray-700">
                            {selectedUserBranchId ? 
                              userBranchOptions.find(branch => branch.id === selectedUserBranchId)?.name || 'Select Branch' 
                              : 'Select Branch'
                            }
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isUserBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isUserBranchDropdownOpen && (
                          <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                            {userBranchOptions.length > 0 ? (
                              userBranchOptions.map((branch) => (
                                <button
                                  key={branch.id}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUserBranchId(branch.id);
                                    setIsUserBranchDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                    selectedUserBranchId === branch.id ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                                  }`}
                                >
                                  {branch.name}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No branches available
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Security Settings */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={newUserData.password}
                      onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      value={newUserData.confirmPassword}
                      onChange={(e) => setNewUserData({...newUserData, confirmPassword: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>
              </div>

              {/* Status Message */}
              {userSubmissionStatus.type && (
                <div className={`p-3 rounded-lg ${
                  userSubmissionStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-700' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  <p className="text-sm">{userSubmissionStatus.message}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setIsAddUserModalOpen(false)}
                  disabled={isSubmittingUser}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingUser || !newUserData.name.trim() || !newUserData.email.trim() || !newUserData.phone_number.trim() || !newUserData.password || !newUserData.confirmPassword}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmittingUser ? 'Creating...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit User Modal */}
      {isEditUserModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsEditUserModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
              <button
                onClick={() => setIsEditUserModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editUserSubmissionStatus.type && (
              <div className={`mb-4 p-3 rounded-lg ${
                editUserSubmissionStatus.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {editUserSubmissionStatus.message}
              </div>
            )}

            <form className="space-y-6" onSubmit={updateUser}>
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={editUserData.name}
                      onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={editUserData.email}
                      onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter email address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={editUserData.phone_number}
                      onChange={(e) => setEditUserData({...editUserData, phone_number: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
              </div>
              
              {/* Account Settings */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role *
                    </label>
                    <div className="relative" data-dropdown>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditUserRoleDropdownOpen(!isEditUserRoleDropdownOpen);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      >
                        <span className="text-sm text-gray-700">
                          {userRoleOptions.find(option => option.value === editUserData.role)?.label}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isEditUserRoleDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isEditUserRoleDropdownOpen && (
                        <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                          {userRoleOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Clear branch assignment if changing away from branch_manager role
                                const newData = { ...editUserData, role: option.value };
                                if (option.value !== 'branch_manager') {
                                  newData.branchId = '';
                                }
                                setEditUserData(newData);
                                setIsEditUserRoleDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                editUserData.role === option.value ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Branch {editUserData.role === 'branch_manager' ? '*' : ''}
                    </label>
                    <div className="relative" data-dropdown>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editUserData.role === 'branch_manager') {
                            setIsEditUserBranchDropdownOpen(!isEditUserBranchDropdownOpen);
                          }
                        }}
                        disabled={editUserData.role !== 'branch_manager'}
                        className={`w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors h-10 ${
                          editUserData.role === 'branch_manager' 
                            ? 'bg-white hover:border-gray-300 cursor-pointer' 
                            : 'bg-gray-50 cursor-not-allowed opacity-60'
                        }`}
                      >
                        <span className={`text-sm ${editUserData.role === 'branch_manager' ? 'text-gray-700' : 'text-gray-400'}`}>
                          {editUserData.role === 'branch_manager' 
                            ? (editUserData.branchId 
                                ? userBranchOptions.find(branch => branch.id === editUserData.branchId)?.name || 'Select a branch'
                                : 'Select a branch'
                              )
                            : 'Not applicable for this role'
                          }
                        </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isEditUserBranchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isEditUserBranchDropdownOpen && editUserData.role === 'branch_manager' && (
                          <div className="absolute left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditUserData({...editUserData, branchId: ''});
                                setIsEditUserBranchDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                !editUserData.branchId ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              Select a branch
                            </button>
                            {userBranchOptions.map((branch) => (
                              <button
                                key={branch.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditUserData({...editUserData, branchId: branch.id});
                                  setIsEditUserBranchDropdownOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                  editUserData.branchId === branch.id ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                                }`}
                              >
                                {branch.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                </div>
              </div>

              {/* Password Change Section */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Change Password (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={editUserData.password}
                      onChange={(e) => setEditUserData({...editUserData, password: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter new password (leave blank to keep current)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={editUserData.confirmPassword}
                      onChange={(e) => setEditUserData({...editUserData, confirmPassword: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Leave password fields blank to keep the current password unchanged.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-6 border-t">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsEditUserModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdatingUser}
                    className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUpdatingUser && (
                      <LoadingSpinner size="sm" text="" color="white" />
                    )}
                    {isUpdatingUser ? 'Updating...' : 'Update User'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete User Confirmation Modal */}
      {userToDelete && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => {
            setUserToDelete(null);
            setIsDeletingUser(false);
          }}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Confirm Delete</h2>
              <button
                onClick={() => {
                  setUserToDelete(null);
                  setIsDeletingUser(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Are you sure you want to delete this user?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{userToDelete.name}</p>
                <p className="text-sm text-gray-600">{userToDelete.email}</p>
                <p className="text-sm text-gray-600 capitalize">{userToDelete.role.replace('_', ' ')}</p>
              </div>
              <p className="text-sm text-red-600 mt-2">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setUserToDelete(null);
                  setIsDeletingUser(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeletingUser}
              >
                Cancel
              </button>
              <button
                onClick={deleteUser}
                disabled={isDeletingUser}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeletingUser && (
                  <LoadingSpinner size="sm" text="" color="white" />
                )}
                {isDeletingUser ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Branch Modal */}
      {isEditBranchModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsEditBranchModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Edit Branch</h2>
              <button
                onClick={() => setIsEditBranchModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="space-y-6" onSubmit={updateBranch}>
              {/* Branch Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Branch Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch Name *
                    </label>
                    <input
                      type="text"
                      value={editBranchData.branch_name}
                      onChange={(e) => setEditBranchData({...editBranchData, branch_name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter branch name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={editBranchData.phone}
                      onChange={(e) => setEditBranchData({...editBranchData, phone: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Branch Address *
                  </label>
                  <textarea
                    rows={3}
                    value={editBranchData.address}
                    onChange={(e) => setEditBranchData({...editBranchData, address: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                    placeholder="Enter complete branch address"
                  ></textarea>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manager Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={editBranchData.manager_name}
                    onChange={(e) => setEditBranchData({...editBranchData, manager_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                    placeholder="Enter manager name"
                  />
                </div>
              </div>

              {/* Status Message */}
              {editBranchSubmissionStatus.type && (
                <div className={`p-3 rounded-lg ${
                  editBranchSubmissionStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-700' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  <p className="text-sm">{editBranchSubmissionStatus.message}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setIsEditBranchModalOpen(false)}
                  disabled={isUpdatingBranch}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdatingBranch || !editBranchData.branch_name.trim() || !editBranchData.address.trim() || !editBranchData.phone.trim()}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isUpdatingBranch ? 'Updating...' : 'Update Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Branch Confirmation Modal */}
      {isDeleteBranchModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsDeleteBranchModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Delete Branch</h2>
              <button
                onClick={() => setIsDeleteBranchModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure?</h3>
              <p className="text-gray-600">
                You are about to delete <strong>{selectedBranchForDelete?.branch_name}</strong>. 
                This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteBranchModalOpen(false)}
                disabled={isDeletingBranch}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteBranch}
                disabled={isDeletingBranch}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isDeletingBranch ? 'Deleting...' : 'Delete Branch'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add New Dealer Modal */}
      {isAddDealerModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsAddDealerModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Add New Dealer</h2>
              <button
                onClick={() => setIsAddDealerModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {dealerError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{dealerError}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleAddDealer}>
              {/* Dealer Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Dealer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dealer Name *
                    </label>
                    <input
                      type="text"
                      value={dealerFormData.name}
                      onChange={(e) => setDealerFormData({...dealerFormData, name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter dealer name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={dealerFormData.phone}
                      onChange={(e) => setDealerFormData({...dealerFormData, phone: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter phone number"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Shop Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Shop Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shop Name *
                    </label>
                    <input
                      type="text"
                      value={dealerFormData.shop_name}
                      onChange={(e) => setDealerFormData({...dealerFormData, shop_name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter shop name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shop Address *
                    </label>
                    <textarea
                      value={dealerFormData.shop_address}
                      onChange={(e) => setDealerFormData({...dealerFormData, shop_address: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                      placeholder="Enter complete shop address"
                      required
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddDealerModalOpen(false);
                    setDealerFormData({ name: '', shop_name: '', shop_address: '', phone: '' });
                    setDealerError(null);
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={dealerSubmitting}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {dealerSubmitting && (
                    <div className="w-4 h-4 animate-spin">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  )}
                  {dealerSubmitting ? 'Adding Dealer...' : 'Add Dealer'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Dealer Modal */}
      {isEditDealerModalOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsEditDealerModalOpen(false)}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Dealer</h2>
                <p className="text-sm text-gray-500 mt-1">Update dealer information</p>
              </div>
              <button
                onClick={() => {
                  setIsEditDealerModalOpen(false);
                  setEditingDealer(null);
                  setDealerFormData({ name: '', shop_name: '', shop_address: '', phone: '' });
                  setDealerError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {dealerError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{dealerError}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleUpdateDealer}>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Dealer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dealer Name *</label>
                    <input
                      type="text"
                      value={dealerFormData.name}
                      onChange={(e) => setDealerFormData({...dealerFormData, name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter dealer name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      value={dealerFormData.phone}
                      onChange={(e) => setDealerFormData({...dealerFormData, phone: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter phone number"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Shop Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name *</label>
                    <input
                      type="text"
                      value={dealerFormData.shop_name}
                      onChange={(e) => setDealerFormData({...dealerFormData, shop_name: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                      placeholder="Enter shop name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shop Address *</label>
                    <textarea
                      value={dealerFormData.shop_address}
                      onChange={(e) => setDealerFormData({...dealerFormData, shop_address: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors resize-none"
                      placeholder="Enter complete shop address"
                      required
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditDealerModalOpen(false);
                    setEditingDealer(null);
                    setDealerFormData({ name: '', shop_name: '', shop_address: '', phone: '' });
                    setDealerError(null);
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={dealerSubmitting}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {dealerSubmitting && (
                    <div className="w-4 h-4 animate-spin">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  )}
                  {dealerSubmitting ? 'Updating...' : 'Update Dealer'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {dealerToDelete && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Dealer</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete <strong>{dealerToDelete.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDealerToDelete(null)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteDealer}
                  disabled={isDeletingDealer}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeletingDealer && (
                    <div className="w-4 h-4 animate-spin">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  )}
                  {isDeletingDealer ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
    </>
  );
};

export default Settings;